import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "mt-2 grid grid-cols-12 gap-x-6" }
const _hoisted_3 = { class: "col-span-8" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center justify-center py-20 bg-white rounded-xl p-6 border border-gray-200 shadow-cards"
}
const _hoisted_5 = {
  key: 1,
  class: "relative border border-gray-200 bg-white shadow sm:rounded-lg"
}
const _hoisted_6 = { class: "absolute top-5 right-5" }
const _hoisted_7 = { class: "absolute z-10 top-0 right-0 h-5 w-5 [&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5" }
const _hoisted_8 = { class: "py-5" }
const _hoisted_9 = { class: "w-full bg-white px-4 py-5 sm:px-6" }
const _hoisted_10 = { class: "text-lg font-bold leading-6 text-gray-900" }
const _hoisted_11 = {
  key: 0,
  class: "min-w-full divide-y divide-gray-300"
}
const _hoisted_12 = {
  scope: "col",
  class: "py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-700 sm:pl-6 lg:pl-8"
}
const _hoisted_13 = ["innerHTML"]
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = { class: "divide-y divide-gray-200 bg-white" }
const _hoisted_16 = { class: "whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6 lg:pl-8" }
const _hoisted_17 = { class: "font-semibold" }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_19 = { class: "font-normal" }
const _hoisted_20 = { class: "whitespace-nowrap px-3 py-4 text-sm text-gray-500" }
const _hoisted_21 = { class: "whitespace-nowrap px-3 py-4 text-sm text-gray-500" }
const _hoisted_22 = { class: "col-span-4 space-y-6" }
const _hoisted_23 = {
  key: 0,
  class: "flex items-center justify-center py-8 rounded-xl p-6 border border-gray-200 bg-white shadow sm:rounded-lg"
}
const _hoisted_24 = {
  key: 1,
  class: "relative border border-gray-200 bg-white shadow sm:rounded-lg"
}
const _hoisted_25 = { class: "absolute top-5 right-5" }
const _hoisted_26 = { class: "absolute z-10 top-0 right-0 h-5 w-5 [&>svg]:stroke-current [&>svg]:h-5 [&>svg]:w-5" }
const _hoisted_27 = { class: "w-full bg-white px-4 py-5 sm:px-6" }
const _hoisted_28 = { class: "mb-5" }
const _hoisted_29 = { class: "text-lg font-bold leading-6 text-gray-900" }
const _hoisted_30 = {
  role: "list",
  class: "divide-y divide-gray-200"
}
const _hoisted_31 = { class: "text-[15px] font-semibold" }
const _hoisted_32 = { class: "text-sm text-gray-600" }

import { computed, onBeforeMount, ref } from "vue";
import { useNoticeStore } from "@/stores/noticeStore";
import { useFilterStore } from "@/stores/filterStore";
import { useUserStore } from "@/stores/userStore";
import { useActivityStore } from "@/stores/activityStore";
import { useSchoolStore } from "@/stores/schoolStore";
import i18n from "@/plugins/i18n";
import { useDataStore } from "@/stores/dataStore";
import { SchoolStructureClassGroup } from "@/interfaces/school-structure.interface";


export default /*#__PURE__*/_defineComponent({
  __name: 'AdminOverviewComponent',
  setup(__props) {

const userStore = useUserStore();
const dataStore = useDataStore();
const filterStore = useFilterStore();
const noticeStore = useNoticeStore();
const schoolStore = useSchoolStore();
const activityStore = useActivityStore(); // activitySummaryPerClassData

const seatsRequestModalOpen = ref(false);

// Utility function to format the average learning time from seconds
const formatTime = (timeInSeconds: number) => {
  if (timeInSeconds < 60) {
    return `${Math.round(timeInSeconds)} ${i18n.global.t(
      "admin_overview_component.sec"
    )}`;
  } else if (timeInSeconds >= 3600) {
    const hours = Math.floor(timeInSeconds / 3600);
    const remainingMinutes = Math.floor((timeInSeconds % 3600) / 60);
    return remainingMinutes > 0
      ? `${hours} ${i18n.global.t(
        "admin_overview_component.hr"
      )} ${remainingMinutes} ${i18n.global.t("admin_overview_component.min")}`
      : `${hours} ${i18n.global.t("admin_overview_component.hr")}`;
  } else {
    const minutes = Math.floor(timeInSeconds / 60);
    return `${minutes} ${i18n.global.t("admin_overview_component.min")}`;
  }
};

// Function to calculate theme class based on average learning time percentage
const calculateLearningTimeTheme = (timeInSeconds: number) => {
  const percentage = (timeInSeconds / (30 * 60)) * 100; // 30 minutes target in seconds
  if (percentage >= 100) {
    return "bg-green-50 text-green-700 ring-green-600/20";
  } else if (percentage >= 66) {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  } else {
    return "bg-red-50 text-red-700 ring-red-600/20";
  }
};

// Function to calculate theme class for percentageDailyTasksCompletion
const calculateDailyTasksTheme = (percentageDailyTasksCompletion: number) => {
  const percentage = (percentageDailyTasksCompletion / 80) * 100; // 80% target

  if (percentage >= 100) {
    return "bg-green-50 text-green-700 ring-green-600/20";
  } else if (percentage >= 66) {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  } else {
    return "bg-red-50 text-red-700 ring-red-600/20";
  }
};

const classesData = computed(() => {
  const selectedGradeClasses = schoolStore.currentSchoolStructure?.classGroups.filter(
    (classGroup: SchoolStructureClassGroup) => classGroup.gradeLevel == filterStore?.selectedFilters?.gradeLevel
  );

  // Order the classes by grade and then name
  selectedGradeClasses?.sort((a: SchoolStructureClassGroup, b: SchoolStructureClassGroup) => {
    if (a.gradeLevel === b.gradeLevel) {
      return a.name.localeCompare(b.name);
    }
    return a.gradeLevel - b.gradeLevel;
  });

  let classData = [];

  for (const classGroup of selectedGradeClasses ?? []) {
    const activityData = activityStore.activitySummaryPerClassData?.find(
      (activity) => activity.classGroupId === classGroup._id
    );

    if (activityData) {
      classData.push({
        _id: activityData.classGroupId,
        name: classGroup.name,
        classTeacher: classGroup.admins[0] || null,
        averageLearningTime: formatTime(activityData.averageLearningTimeWeek),
        averageLearningTimeClass: calculateLearningTimeTheme(
          activityData.averageLearningTimeMonth
        ),
        percentageDailyTasksCompletion: `${Math.round(
          activityData.percentageDailyTasksCompletionWeek
        )} %`,
        dailyTasksTheme: calculateDailyTasksTheme(
          Math.round(activityData.percentageDailyTasksCompletionWeek)
        ),
      });
    }
  }

  return classData;
});

onBeforeMount(async () => {
  await noticeStore.fetchNotices(filterStore.selectedFilters.school as string);
  await dataStore.fetchAllClassesData();
});

return (_ctx: any,_cache: any) => {
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_rl_icon = _resolveComponent("rl-icon")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (_unref(activityStore).loader.fetchActivitySummaryPerClassData === true)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_component_spinner_loader, { color: 'text-blue-600 dark:text-blue-600' })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_7, [
                  _createVNode(_component_rl_icon, {
                    icon: 'rl-info-circle',
                    title: 'rl info circle',
                    "aria-hidden": "true"
                  })
                ])), [
                  [
                    _directive_tooltip,
                    _ctx.$t('admin_view.tooltips.activity_by_class_card'),
                    "left",
                    { tooltip: true }
                  ]
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("h3", _hoisted_10, _toDisplayString(_ctx.$t("admin_overview_component.activity_by")), 1)
                ]),
                (
              _unref(activityStore).activitySummaryPerClassData &&
              _unref(activityStore).activitySummaryPerClassData.length > 0
            )
                  ? (_openBlock(), _createElementBlock("table", _hoisted_11, [
                      _createElementVNode("thead", null, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", _hoisted_12, _toDisplayString(_ctx.$t("admin_overview_component.class")), 1),
                          _createElementVNode("th", {
                            scope: "col",
                            class: "px-3 py-3.5 text-left text-sm font-semibold text-gray-700",
                            innerHTML: _ctx.$t('admin_overview_component.average_min')
                          }, null, 8, _hoisted_13),
                          _createElementVNode("th", {
                            scope: "col",
                            class: "px-3 py-3.5 text-left text-sm font-semibold text-gray-700",
                            innerHTML: _ctx.$t('admin_overview_component.daily_tasks')
                          }, null, 8, _hoisted_14)
                        ])
                      ]),
                      _createElementVNode("tbody", _hoisted_15, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(classesData.value, (classGroup) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: classGroup._id
                          }, [
                            _createElementVNode("td", _hoisted_16, [
                              _createElementVNode("span", _hoisted_17, _toDisplayString(classGroup.name), 1),
                              _hoisted_18,
                              _createElementVNode("span", _hoisted_19, _toDisplayString(classGroup.classTeacher
                          ? `${classGroup.classTeacher.firstName} ${classGroup.classTeacher.lastName}`
                          : "No Teacher Assigned"), 1)
                            ]),
                            _createElementVNode("td", _hoisted_20, [
                              _createElementVNode("span", {
                                class: _normalizeClass([
                      'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
                      classGroup.averageLearningTimeClass,
                    ])
                              }, _toDisplayString(classGroup.averageLearningTime), 3)
                            ]),
                            _createElementVNode("td", _hoisted_21, [
                              _createElementVNode("span", {
                                class: _normalizeClass([
                      'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
                      classGroup.dailyTasksTheme,
                    ])
                              }, _toDisplayString(classGroup.percentageDailyTasksCompletion), 3)
                            ])
                          ]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]))
      ]),
      _createElementVNode("div", _hoisted_22, [
        (_unref(noticeStore).loader.fetchNotices === true)
          ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createVNode(_component_spinner_loader, { color: 'text-blue-600 dark:text-blue-600' })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_24, [
              _createElementVNode("div", _hoisted_25, [
                _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_26, [
                  _createVNode(_component_rl_icon, {
                    icon: 'rl-info-circle',
                    title: 'rl info circle',
                    "aria-hidden": "true"
                  })
                ])), [
                  [
                    _directive_tooltip,
                    _ctx.$t('admin_view.tooltips.noticeboard_card'),
                    "left",
                    { tooltip: true }
                  ]
                ])
              ]),
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("h3", _hoisted_29, _toDisplayString(_ctx.$t("admin_overview_component.noticeboard")), 1)
                ]),
                _createElementVNode("ul", _hoisted_30, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(noticeStore).notices, (notice, index) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: index,
                      class: "py-4"
                    }, [
                      _createElementVNode("h5", _hoisted_31, _toDisplayString(notice.title), 1),
                      _createElementVNode("p", _hoisted_32, _toDisplayString(notice.description), 1)
                    ]))
                  }), 128))
                ])
              ])
            ]))
      ])
    ])
  ]))
}
}

})